// FE-234: changing a password through Azure AD B2C's password-reset flow.
// The webapp never sees the password: the user types it on B2C's page in
// MSAL's popup. This module holds the pure rules, with no MSAL and no
// imports: the flow's authority and the fixed messages the Settings link shows.

// B2C's password-reset flow (B2C_1_changepassword), the same in each tenant:
// a Password reset (Recommended) user flow, with an email code.
export const PASSWORD_CHANGE_FLOW = "B2C_1_changepassword";

// Every message the link shows. None echoes MSAL's or B2C's own text, which
// can carry a correlation id and a trace.
export const PASSWORD_CHANGE_TEXT = Object.freeze({
  help: "You'll get a one-time code by email, then choose a new password in a Whygrene sign-in window. This app never sees your password.",
  changed: "Password changed. Signing you out: sign in with your new password.",
  cancelled: "Your password wasn't changed.",
  popup_blocked:
    "Your browser blocked the sign-in window. Allow pop-ups for this site and try again.",
  busy: "Sign-in is busy. Try again in a moment.",
  failed: "Couldn't open the password page. Try again later.",
});

// B2C's code when the user presses Cancel on its page. It's matched in the
// error's message whatever the error's code.
const B2C_USER_CANCELLED = "AADB2C90091";

// https://<host>/<tenant>[/...]/<policy>: group 1 is everything before the policy.
const AUTHORITY_WITH_POLICY = /^(https?:\/\/[^/?#]+\/[^?#]+)\/[^/?#]+$/;

/**
 * The password-reset flow's authority: the sign-in authority with its last
 * path segment (the sign-in policy) swapped for the flow. The host and tenant
 * stay. Returns null when the authority has no policy segment.
 */
export function passwordChangeAuthority(signInAuthority) {
  if (typeof signInAuthority !== "string") return null;
  const match = AUTHORITY_WITH_POLICY.exec(signInAuthority.trim().replace(/\/+$/, ""));
  return match ? `${match[1]}/${PASSWORD_CHANGE_FLOW}` : null;
}

function outcome(name) {
  return { outcome: name, text: PASSWORD_CHANGE_TEXT[name] };
}

/**
 * What the link shows after MSAL's popup fails: { outcome, text }, where
 * outcome is cancelled, popup_blocked, busy or failed, and text is that
 * outcome's fixed message.
 */
export function passwordChangeOutcome(error) {
  const code = typeof error?.errorCode === "string" ? error.errorCode : "";
  const message = typeof error?.errorMessage === "string" ? error.errorMessage : "";

  if (code === "user_cancelled") return outcome("cancelled");
  if (message.includes(B2C_USER_CANCELLED)) return outcome("cancelled");
  if (code === "popup_window_error" || code === "empty_window_error") {
    return outcome("popup_blocked");
  }
  if (code === "interaction_in_progress") return outcome("busy");
  return outcome("failed");
}
